'use client';

import { useEffect, useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useUnsettledBetListInfo, useUnsettledStatusCountInfo } from '@/hooks/query';
import { useBetsContext } from '@/features/bets/contexts/bets-context';
import { PageLoadingSpinner } from '@/components/core/loading-spinner';
import { EmptyContentMessage } from '@/components/layout/empty-content-message';
import clsx from 'clsx';
import { LoadingIcon } from '@/components/icons/svg-icons';
import { MainFooter } from '@/components/layout/main-footer';
import { CashOutProvider } from '../contexts/cashout-context';
import { BetsList } from '../components/bets-list';
import { betsStyle } from '../bets.style';
import { ViewEnum } from '@/types';
import { useAppearanceStore } from '@/store/appearance';
import webStorageKeys from '@/constants/web-storage-keys';
import { isSupportBackToMatch } from '@/utils/bets-util';
import { BookingBetsProvider } from '../contexts/booking-bets-context';

type UnsettledProps = {
	isAside?: boolean;
	asideType?: number;
	isFetchingCategory?: boolean;
	isOutSabaCoinSelected?: boolean;
}

export const Unsettled = ({
	isAside,
	asideType,
	isFetchingCategory,
	isOutSabaCoinSelected
}: UnsettledProps) => {
	const t = useTranslations('General');

	const { dataTypeUnsettled: dataType, isSabaCoinSelected } = useBetsContext();
	const [type, setType] = useState(isAside ? asideType! : dataType);
	const { bets, refetchUnsettledBetList, isLoading, isError } = useUnsettledBetListInfo(type === 1 ? 8 : type, isSabaCoinSelected || (type === 1 && isAside));
	const tickets = bets?.Ticket;

	const view = useAppearanceStore(state => state.view);

	// 偵測有沒有 waiting ticket
	const totalWaitingTickets = tickets && tickets.reduce((total, betsData) => {
		const waitingCountInBetsData = betsData.betList.filter(ticketData => ticketData.Status === 'waiting').length;
		return total + waitingCountInBetsData;
	}, 0);

	const { statusCount, refetchUnsettledStatusCount } = useUnsettledStatusCountInfo(totalWaitingTickets!);
	const waitingCountRef = useRef<ReturnType<typeof setInterval>>();

	useEffect(() => {
		clearInterval(waitingCountRef.current);
		if (statusCount) {
			if (statusCount?.WC > 0) {
				waitingCountRef.current = setInterval(() => {
					refetchUnsettledStatusCount();
					refetchUnsettledBetList();
				}, 6000);
			}
		}
		return () => {
			clearInterval(waitingCountRef.current);
		}
	}, [statusCount]);

	useEffect(() => {
		setType(isAside ? asideType! : dataType);
	}, [dataType, asideType]);

	useEffect(() => {
		if ((isAside && !isFetchingCategory || !isAside) && type) {
			refetchUnsettledBetList();
		}
	}, [type, isFetchingCategory]);

	/** 取得需要顯示返回賽事提示的首個 ticket id */
	const getShowBackToMatchTipTicketId = () => {
		if (tickets) {
			for (const ticket of tickets) {
				const firstTicket = ticket.betList.find(item => !Boolean(item.ParlayList) && isSupportBackToMatch(item.SportID, item.Bettype, item.BackLeagueId, item.BackMatch));
				if (firstTicket) {
					return firstTicket.TransID;
				}
			}
		}
		return '';
	}

	const showBackToMatchTipTicketId = localStorage.getItem(webStorageKeys.HAS_SHOWN_BACK_TO_MATCH_TIP) !== 'true' ? getShowBackToMatchTipTicketId() : '';

	if (type === 0 || isError) {
		return (
			<div className={clsx('mybets', !isAside && 'mybets--page')}>
				<EmptyContentMessage
					type="bets"
					title={t('lbl_NoOpenBets')}
				>
				</EmptyContentMessage>
			</div>
		)
	}

	if (isLoading) {
		return isAside ? (
			<div className="loading-circle"><LoadingIcon /></div>
		) : <PageLoadingSpinner />
	}

	return (
		<>
			{view === ViewEnum.mars ? (
				<section className={betsStyle.count}>
					<div>{t('lbl_TotalStakes')}:
						<span className={clsx(betsStyle.textAmount, betsStyle.amountSize)}>
							{bets?.StakeCount}
						</span>
					</div>
				</section>
			) : null}
			<div className={clsx('mybets', !isAside && 'mybets--page', betsStyle.mybets)}>
				{tickets ? (
					<BookingBetsProvider>
						<CashOutProvider initCashOutList={bets.CashOutTicketInfoList}>
							{tickets.map((ticket, ticketIndex) => (
								<BetsList
									key={ticketIndex}
									isCashout={false}
									ticket={ticket}
									ticketIndex={ticketIndex}
									isAside={isAside}
									showBackToMatchTipTicketId={showBackToMatchTipTicketId}
									isOutSabaCoinSelected={isOutSabaCoinSelected}
								/>
							))}
							<MainFooter />
						</CashOutProvider>
					</BookingBetsProvider>
				) : (
					<EmptyContentMessage
						type="bets"
						title={t('lbl_NoOpenBets')}
					>
					</EmptyContentMessage>
				)}
			</div>
		</>
	)
}